import { useState, ChangeEvent, FocusEvent, FormEvent, useContext } from "react"
import { Box, TextField, Alert } from "@mui/material"
import * as Yup from "yup"
import { FirebaseError } from "firebase/app"
import BasicButton from "../assets/components/BasicButton"
import { useCurrentUser } from "../hooks/contexts/currentUserContext"
import { changePassword } from "../firebase/authenticationApi"
import { User } from "firebase/auth"

import { PolyglotContext } from "../polyglot/polyGlotContext"

interface FormData {
    curPassword: string
    newPassword: string
}

type AlertColor = "error" | "warning" | "info" | "success"

const validationSchema: Yup.Schema<FormData> = Yup.object().shape({
    curPassword: Yup.string().required("Obligatorisk").min(8, "Måste vara minst 8 karaktärer"),
    newPassword: Yup.string().required("Obligatorisk").min(8, "Måste vara minst 8 karaktärer"),
})

const ChangePassword = () => {
    const { polyglot } = useContext(PolyglotContext)

    const initialFormState: FormData = {
        curPassword: "",
        newPassword: "",
    }

    const initialErrors: FormData = {
        curPassword: "",
        newPassword: "",
    }

    const initialAlert = {
        severity: "",
        text: "",
    }

    const currentUser = useCurrentUser()
    const [formData, setFormData] = useState(initialFormState)
    const [errors, setErrors] = useState(initialErrors)
    const [alert, setAlert] = useState(initialAlert)
    const [handlingSubmit, setHandlingSubmit] = useState(false)

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
        setFormData({ ...formData, [e.target.name]: e.target.value })
    }

    const handleBlur = async (e: FocusEvent<HTMLInputElement>) => {
        const name = e.target.name
        const value = e.target.value

        try {
            await (validationSchema as Yup.ObjectSchema<FormData>).validateAt(name, { [name]: value })
            setErrors((prevErrors) => ({ ...prevErrors, [name]: "" }))
        } catch (error: any) {
            if (Yup.ValidationError.isError(error)) {
                setErrors((prevErrors) => ({
                    ...prevErrors,
                    [name]: error.errors[0],
                }))
            }
        }
    }

    const handleChangePassword = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault()

        // Indicate handling submit
        setHandlingSubmit(true)

        // Reset alert
        setAlert(initialAlert)

        try {
            // Validate form data
            await validationSchema.validate(formData, { abortEarly: false })
            setErrors(initialErrors) // Reset errors

            // Change password
            await changePassword(currentUser?.auth as User, formData.curPassword, formData.newPassword)

            // Report success
            setAlert({ severity: "success", text: polyglot.t("components.changePassword.form.validation.alerts.success") })
            setFormData(initialFormState) // Reset form
        } catch (error: any) {
            if (Yup.ValidationError.isError(error)) {
                // Validation error
                const newErrors: FormData = { ...initialErrors }
                error.inner.forEach((err: any) => {
                    newErrors[err.path as keyof FormData] = err.message
                })
                setErrors(newErrors)

                // Report validation failure
                setAlert({ severity: "error", text: polyglot.t("components.changePassword.form.validation.alerts.validationFail") })
            } else if (error instanceof FirebaseError) {
                // Firebase error
                switch (error.code) {
                    case "user-not-found":
                    case "invalid-email":
                    case "auth/invalid-login-credentials":
                    case "auth/wrong-password":
                        // Report user/password error
                        setAlert({ severity: "error", text: polyglot.t("components.changePassword.validation.form.alerts.userPasswrodError1") })
                        setErrors({ ...errors, curPassword: polyglot.t("components.changePassword.validation.form.alerts.userPasswordError2") })
                        break
                    case "auth/weak-password":
                        // Report weak password error
                        setAlert({ severity: "error", text: polyglot.t("components.changePassword.validation.form.alerts.weakPassword") })
                        break
                    default:
                        // Report general Firebase error
                        setAlert({ severity: "error", text: polyglot.t("components.changePassword.validation.form.alerts.firebaseError") })
                }
            } else {
                // Other error
                // Report general failure
                setAlert({ severity: "error", text: polyglot.t("components.changePassword.validation.alerts.form.generalError") })
            }
        }

        // Indicate done handling submit
        setHandlingSubmit(false)
    }

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                width: "100%",
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                    gap: "10px",
                    width: "100%",
                }}
            >
                {alert.text ? (
                    <Alert
                        severity={alert.severity as AlertColor}
                        onClose={() => {
                            setAlert(initialAlert)
                        }}
                    >
                        {alert.text}
                    </Alert>
                ) : null}
                <form onSubmit={handleChangePassword}>
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "flex-start",
                            gap: "10px",
                            width: "100%",
                        }}
                    >
                        <TextField
                            label={polyglot.t("components.changePassword.form.currentPasswordLabel")}
                            name="curPassword"
                            type="password"
                            variant="standard"
                            value={formData.curPassword}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={!!errors.curPassword}
                            helperText={errors.curPassword}
                            disabled={handlingSubmit}
                        />
                        <TextField
                            label={polyglot.t("components.changePassword.form.newPasswordLabel")}
                            name="newPassword"
                            type="password"
                            variant="standard"
                            value={formData.newPassword}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={!!errors.newPassword}
                            helperText={errors.newPassword}
                            disabled={handlingSubmit}
                        />
                        <BasicButton title={polyglot.t("components.changePassword.form.submitBtn")} type="submit" disabled={handlingSubmit} progress={handlingSubmit} />
                    </Box>
                </form>
            </Box>
        </Box>
    )
}

export default ChangePassword
